import React, { useRef, useState } from "react";
import NumberFormat from "react-number-format";

import { Button, Paper, Stack, TextField } from "@mui/material";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { SelectAutocomplete } from "components/forms";
import { useInvestorsSelector } from "store";
import { CurrencyType } from "store/types";

import { Errors, useGroupsForm } from "./GroupsFormContext";

interface Props {
  round_id: number | string;
  group_id: number;

  isAllowedNegative: boolean;
  valuePrefix?: string;
  valueSuffix?: string;
  currency?: CurrencyType | null;
  date?: string | null;
  accounting_date?: string | null;
  effective_range_from?: string | null;
  effective_range_to?: string | null;
}

export const ZeroMeasureData: React.FC<Props> = ({
  isAllowedNegative,
  valuePrefix,
  valueSuffix,
  group_id,
  round_id,
  currency,
  date,
  accounting_date,
  effective_range_from,
  effective_range_to
}) => {
  const investors = useInvestorsSelector().map((item) => ({ id: item.id, name: item.name, group: item.group || null }));
  const { createMeasureData } = useGroupsForm();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [investorError, setInvestorError] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [investor, setInvestor] = useState<{ id: number; name: string; group: string | null } | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInvestorSelect = ({
    investor_id,
    investor_name,
    investor_group
  }: {
    investor_id: number;
    investor_name: string;
    investor_group: string | null;
  }) => {
    try {
      createMeasureData({
        group_id,
        round_id,
        investor_id,
        investor_name,
        investor_group,
        value,
        ...(date && { date }),
        ...(currency && { currency }),
        ...(accounting_date && { accounting_date }),
        ...(effective_range_from && { effective_range_from }),
        ...(effective_range_to && { effective_range_to })
      });
      setInvestor(null);
      setValue("");
      setInvestorError("");
      inputRef.current?.blur(); // unfocus the input
    } catch (error) {
      if (error === Errors.SAME_INVESTOR_PER_ROUND) {
        setInvestor({ id: investor_id, name: investor_name, group: investor_group || null });
        setInvestorError(Errors.SAME_INVESTOR_PER_ROUND);
      }
    }
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          flexGrow: 1,
          alignItems: "flex-start",
          gap: "10px",
          ".MuiIconButton-root": {
            opacity: 0,
            transition: "all 0.2s linear"
          },
          ":hover": {
            ".MuiIconButton-root": {
              opacity: 1
            }
          }
        }}
      >
        <SelectAutocomplete
          sx={{ maxWidth: 195 }}
          options={investors}
          fullWidth
          renderInput={(params) => (
            <TextField
              inputRef={inputRef}
              {...params}
              label="Investor"
              placeholder="Type to add investors"
              error={Boolean(investorError?.length)}
              helperText={investorError}
            />
          )}
          renderOption={(
            props: React.HTMLAttributes<HTMLLIElement>,
            option: { id: number; name: string; group: string | null }
          ) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          setValue={(value) => {
            if (!value) return;
            handleInvestorSelect({
              investor_id: value.id,
              investor_name: value.name,
              investor_group: value.group || null
            });
          }}
          value={investor}
          getOptionLabel={(option) => option.name}
          compareParam={(p) => p.name}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    boundary: "viewport",
                    padding: 8
                  }
                },
                {
                  name: "flip",
                  enabled: true,
                  options: {
                    fallbackPlacements: ["top-start", "bottom-end", "top-end"]
                  }
                }
              ]
            },
            paper: {
              component: ({ children, ...props }: any) => {
                return (
                  <Paper
                    {...props}
                    sx={{
                      width: "100%",
                      backgroundColor: "white",
                      zIndex: "10",
                      borderRadius: "10px",
                      "& .MuiAutocomplete-listbox": {
                        maxHeight: "28vh",
                        "& .MuiAutocomplete-option[aria-disabled='true']": {
                          opacity: 1,
                          "&.Mui-focused": {
                            opacity: 1
                          }
                        },
                        "& .MuiAutocomplete-option[aria-selected='true']": {
                          bgcolor: "#edf1f9",
                          "&.Mui-focused": {
                            bgcolor: "#edf1f9"
                          }
                        }
                      },
                      "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                        bgcolor: "#edf1f9"
                      }
                    }}
                  >
                    <>
                      {children}
                      <Button
                        onMouseDown={() => setIsDrawerOpen(true)}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          height: "50px",
                          backgroundColor: "#fff",
                          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                          borderRadius: "0 0 10px 10px"
                        }}
                        fullWidth
                      >
                        Add Investor
                      </Button>
                    </>
                  </Paper>
                );
              }
            }
          }}
        />

        <NumberFormat
          thousandSeparator
          isNumericString
          variant="outlined"
          prefix={valuePrefix}
          suffix={valueSuffix}
          decimalScale={2}
          allowNegative={isAllowedNegative}
          label="Value"
          sx={{
            maxWidth: 165,
            flexGrow: 1,
            ".MuiInputBase-root": {
              pr: 0,
              overflow: "hidden"
            },
            ".MuiInputBase-input": {
              padding: "8.5px 8px"
            },
            ".MuiButtonBase-root": {
              display: "none"
            },
            "&:hover": {
              ".MuiButtonBase-root": {
                display: "block"
              }
            }
          }}
          customInput={TextField}
          value={value}
          onValueChange={({ value }) => setValue(value)}
        />
      </Stack>
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={(response) => {
          handleInvestorSelect({
            investor_id: response.id,
            investor_name: response.name,
            investor_group: response.group || null
          });
        }}
      />
    </>
  );
};
